import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLooksStore } from '@/stores/looks.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count, money } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import { ImageIcon, PlusIcon, SearchIcon, SparkleIcon } from '@/components/icons';
import { ListCard, ListToolbar, LoadMore, Page, Thumb } from '@/components/page';
import { Alert, ButtonLink, EmptyState, Input, Pill, Skeleton } from '@/components/ui';

/**
 * Looks (R17, docs/01-Business/Looks.md): products that go together, each with
 * how much — shown on their products' pages and the home page, and added to
 * the basket in one step. No price of their own: the total is the lines.
 *
 * A grid of the looks as the shop window shows them — the model photograph
 * first — because a look is recognised by its picture long before its title.
 * Each opens on a page of its own.
 */
export function LooksPage() {
  const { items, loading, loadingMore, error, nextCursor, search, load, loadMore } = useLooksStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const [query, setQuery] = useState(search);

  useEffect(() => {
    void load();
  }, [load, locale]);

  useEffect(() => {
    if (query === search) return;
    const timer = setTimeout(() => void load(query), 350);
    return () => clearTimeout(timer);
  }, [query, search, load]);

  const newLook = (
    <ButtonLink to="/looks/new">
      <PlusIcon className="size-4" />
      {t('newLook')}
    </ButtonLink>
  );

  return (
    <Page title={t('looks')} subtitle={t('looksSubtitle')} actions={newLook}>
      <ListCard>
        <ListToolbar>
          <div className="relative max-w-sm">
            <span className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-ink-faint">
              <SearchIcon className="size-4" />
            </span>
            <Input
              type="search"
              placeholder={t('searchLooks')}
              aria-label={t('searchLooks')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="ps-9"
            />
          </div>
        </ListToolbar>

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading && items.length === 0 ? (
          <div className="grid gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} className="aspect-[4/5]" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState title={t('noLooks')} body={t('noLooksHint')} icon={<SparkleIcon />} action={newLook} />
        ) : (
          <ul className="grid gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {items.map((look) => (
              <li key={look.id}>
                <Link
                  to={`/looks/${look.id}`}
                  className="group flex h-full flex-col overflow-hidden rounded-card border border-line bg-surface transition-all hover:border-brand-300 hover:shadow-raised"
                >
                  <div className="relative aspect-[4/5] overflow-hidden bg-surface-sunken">
                    {look.portraitUrl ? (
                      <img
                        src={mediaUrl(look.portraitUrl)}
                        alt=""
                        loading="lazy"
                        className="size-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.03]"
                      />
                    ) : (
                      <span className="flex size-full items-center justify-center text-ink-faint">
                        <ImageIcon className="size-8" />
                      </span>
                    )}
                    <span className="absolute start-3 top-3 flex gap-1.5">
                      {look.isActive === false ? <Pill>{t('hidden')}</Pill> : null}
                      {look.category ? <Pill tone="info">{look.category.name}</Pill> : null}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col gap-3 p-4">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-ink">{look.title}</p>
                      {look.blurb ? <p className="mt-0.5 line-clamp-2 text-xs text-ink-muted">{look.blurb}</p> : null}
                    </div>
                    <div className="mt-auto flex items-center justify-between gap-3">
                      <span className="flex -space-x-2 rtl:space-x-reverse" aria-hidden>
                        {look.items.slice(0, 5).map((item) => (
                          <Thumb
                            key={item.variantId}
                            src={mediaUrl(item.imageUrl)}
                            swatch={item.swatchHex}
                            size="xs"
                            className="rounded-full border-2 border-surface"
                          />
                        ))}
                      </span>
                      <span className="text-end">
                        <span data-numeric className="block text-sm font-semibold text-ink">
                          {money(look.total, locale, look.currency)}
                        </span>
                        <span className="block text-[11px] text-ink-muted">
                          {t('lookPieces', { count: count(look.items.length, locale) })}
                        </span>
                      </span>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}

        <LoadMore visible={Boolean(nextCursor)} loading={loadingMore} onClick={() => void loadMore()} />
      </ListCard>
    </Page>
  );
}
